import { useMemo } from "react";

import { useSelector } from "react-redux";

import type { CompetitorsDataTypeMap } from "app/pages/benchmark-tracker/components/Report/CompetitorsModal/type";
import { createAudienceTableData } from "app/pages/benchmark-tracker/helpers/createAudienceTableData";
import { createRankingTableData } from "app/pages/benchmark-tracker/helpers/createRankingTableData";
import { createTrafficShareTableData } from "app/pages/benchmark-tracker/helpers/createTrafficShareTableData";
import { selectSelectedBenchmark } from "app/pages/benchmark-tracker/store/selectors/entitySelectors";
import { selectAssetsImages, selectCompetitorByName } from "app/pages/benchmark-tracker/store/selectors/reportSelectors";
import type {
    AudienceApiSchema,
    RankingDataSchema,
    TrafficShareMetricSchema,
} from "app/pages/benchmark-tracker/types/api.types";
import { BenchmarkMetricName } from "app/pages/benchmark-tracker/types/api.types";

export const useModalTableData = (
    metricName: keyof CompetitorsDataTypeMap,
    isRelative: boolean,
    isInverted: boolean,
) => {
    const competitorsData = useSelector(selectCompetitorByName(metricName));
    const { primaryDomains } = useSelector(selectSelectedBenchmark) ?? {};
    const domainImages = useSelector(selectAssetsImages);
    const primaryDomain = primaryDomains?.[0];

    return useMemo(() => {
        if (!competitorsData) {
            return [];
        }

        switch (metricName) {
            case BenchmarkMetricName.trafficShare:
                return createTrafficShareTableData(
                    primaryDomain ?? "",
                    competitorsData as TrafficShareMetricSchema,
                    domainImages,
                );

            case BenchmarkMetricName.audienceReach:
                return createAudienceTableData(primaryDomain ?? "", competitorsData as AudienceApiSchema, domainImages);

            default:
                return createRankingTableData(
                    primaryDomain ?? "",
                    competitorsData as RankingDataSchema,
                    domainImages,
                    isRelative,
                    isInverted,
                );
        }
    }, [competitorsData, domainImages, isInverted, isRelative, metricName, primaryDomain]);
};
